import styled from "styled-components";
import PropTypes from "prop-types";
import { useState } from "react";

const Wrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  grid-gap: 8px;
`;

const Label = styled.label`
  cursor: pointer;
  min-width: 0;
`

const Element = styled.div`
  width: 60px;
  height: 32px;
  border-radius: 16px;
  color: var(--wp--preset--color--steel-grey-400);
  background-color: currentColor;
  transition: color .25s ease-out;
`
const ElementWrapper = styled.div`
  position: relative;
`;

const Switch = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  top: 0;
  left: 0;
  transition: left .25s ease-out, box-shadow .25s ease-out;
  margin: 4px;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  color: inherit;
  background-color: white;
  box-shadow: 0 0 1px 0 rgba(105, 109, 112, 0.40), 0 1px 2px 0 rgba(105, 109, 112, 0.27);
`;

const Input = styled.input`
  position: absolute;
  z-index: 1;
  top: 0;
  left: 0;
  right: auto;
  -webkit-appearance: none;
  appearance: none;
  width: 100%;
  height: 100%;
  opacity: 0.4;
  margin: 0;
  cursor: pointer;

  &:checked + ${ Element } {
    color: var(--wp--preset--color--blue-400);

    & > ${ Switch } {
      left: 28px;
    }
  }
  
  &:disabled {
    pointer-events: none;

    & + ${ Element } {
      color: var(--wp--preset--color--steel-grey-300);

      & > ${ Switch } {
        box-shadow: none;
      }
    }
  }

  @media (hover: hover) {
    &:hover + ${ Element } {
      color: var(--wp--preset--color--steel-grey-600);
    }

    &:checked:hover + ${ Element } {
      color: var(--wp--preset--color--blue-500);
    }
  }
`;

function ToggleSwitch( {
						   name,
						   id,
						   ariaLabel,
						   defaultChecked,
						   value,
						   label,
						   onChange,
						   disabled,
					   } ) {
	const [ checked, setChecked ] = useState( defaultChecked );

	function handleChange( event ) {
		setChecked( event.target.checked );
		if ( onChange ) onChange( event );
	}

	if ( label.checked && label.unchecked ) {
		label = checked ? label.checked : label.unchecked;
	}

	return (
		<Wrapper>
			{ label && <Label htmlFor={ id }>{ label }</Label> }
			<ElementWrapper>
				<Input
					value={ value }
					checked={ checked }
					name={ name }
					id={ id }
					disabled={ disabled }
					type="checkbox"
					onChange={ ( event ) => handleChange( event ) }
					aria-label={ ariaLabel }
				/>
				<Element>
					<Switch>
						{( checked ?
						<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
							<path fillRule="evenodd" clipRule="evenodd"
								  d="M13.7374 3.59583C13.9606 3.81899 13.9606 4.1808 13.7374 4.40396L6.40407 11.7373C6.18091 11.9604 5.8191 11.9604 5.59595 11.7373L2.26261 8.40396C2.03946 8.1808 2.03946 7.81899 2.26261 7.59583C2.48577 7.37268 2.84758 7.37268 3.07073 7.59583L6.00001 10.5251L12.9293 3.59583C13.1524 3.37268 13.5142 3.37268 13.7374 3.59583Z"
								  fill="currentColor" stroke="currentColor" strokeLinecap="round"
								  strokeLinejoin="round"/>
						</svg> :
						<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
							<path fillRule="evenodd" clipRule="evenodd"
								  d="M3.59594 3.59583C3.8191 3.37268 4.18091 3.37268 4.40406 3.59583L8 7.19177L11.5959 3.59583C11.8191 3.37268 12.1809 3.37268 12.4041 3.59583C12.6272 3.81899 12.6272 4.1808 12.4041 4.40396L8.80812 7.99989L12.4041 11.5958C12.6272 11.819 12.6272 12.1808 12.4041 12.404C12.1809 12.6271 11.8191 12.6271 11.5959 12.404L8 8.80802L4.40406 12.404C4.18091 12.6271 3.8191 12.6271 3.59594 12.404C3.37278 12.1808 3.37278 11.819 3.59594 11.5958L7.19188 7.99989L3.59594 4.40396C3.37278 4.1808 3.37278 3.81899 3.59594 3.59583Z"
								  fill="currentColor" stroke="currentColor" strokeLinecap="round"
								  strokeLinejoin="round"/>
						</svg>
						)}
					</Switch>
				</Element>
			</ElementWrapper>
		</Wrapper>
	);
}

ToggleSwitch.propTypes = {
	name: PropTypes.string,
	id: PropTypes.string,
	ariaLabel: PropTypes.string,
	defaultChecked: PropTypes.bool,
	value: PropTypes.string,
	label: PropTypes.oneOfType( [
		PropTypes.node,
		PropTypes.shape( { checked: PropTypes.node, unchecked: PropTypes.node } ),
	] ).isRequired,
	disabled: PropTypes.bool,
	onChange: PropTypes.func,
}

ToggleSwitch.defaultProps = {
	defaultChecked: false,
	id: null,
	disabled: false,
	onChange: null,
};

export default ToggleSwitch;
